import Vue from 'vue'
import Vuex from 'vuex'
import VuexPersistence from 'vuex-persist'
import { setAccessToken, clearAccessToken, setRefreshToken, clearRefreshToken } from '@/services/http'
import { listBeats } from '@/services/beatsService'
import { listSongs } from '@/services/songsService'
import { fetchArtists } from '@/services/api'
import { getMyProfile, updateProfile, getAvatarUploadUrl, confirmAvatarUpload } from '@/services/profileService'
import { logout as apiLogout } from '@/services/authService'
import { changeUserRole } from '@/services/adminService'
import notificationsModule from './notifications'
import collaborationsModule from './collaborations'
import paymentMethodsModule from './paymentMethods'
import uploadDraftsModule from './uploadDrafts'

Vue.use(Vuex)

var BEATS_TTL = 30 * 24 * 60 * 60 * 1000

function isFresh (timestamp, ttl) {
  return timestamp && (Date.now() - timestamp) < (ttl || BEATS_TTL)
}

var vuexLocal = new VuexPersistence({
  storage: window.localStorage,
  supportCircular: true
})

var beatsModule = {
  namespaced: true,
  state: function () {
    return {
      items: [],
      page: 1,
      hasMore: true,
      loading: false,
      filters: { genre: 'All', tempo: 'All', licence: 'All', search: '' },
      lastFetched: null
    }
  },
  getters: {
    FILTERED_BEATS: function (state) {
      var items = state.items
      var f = state.filters

      if (f.licence && f.licence !== 'All') {
        if (f.licence === 'Free') {
          items = items.filter(function (b) { return !b.price && !b.priceKes })
        } else if (f.licence === 'Paid') {
          items = items.filter(function (b) { return !!(b.price || b.priceKes) })
        }
      }

      if (f.search && f.search.trim()) {
        var q = f.search.trim().toLowerCase()
        items = items.filter(function (b) {
          return (
            (b.title && b.title.toLowerCase().indexOf(q) !== -1) ||
            (b.genre && b.genre.toLowerCase().indexOf(q) !== -1) ||
            (b.genreTags && b.genreTags.join(' ').toLowerCase().indexOf(q) !== -1)
          )
        })
      }

      return items
    },
    HOME_BEATS: function (state, getters) {
      return getters.FILTERED_BEATS.slice(0, 8)
    },
    IS_LOADING: function (state) {
      return state.loading
    },
    HAS_MORE: function (state) {
      return state.hasMore
    }
  },
  mutations: {
    APPEND_BEATS: function (state, beats) {
      var existing = {}
      state.items.forEach(function (b) { existing[b.id || b.title] = true })
      var newBeats = beats.filter(function (b) { return !existing[b.id || b.title] })
      state.items = state.items.concat(newBeats)
    },
    RESET_PAGINATION: function (state) {
      state.items = []
      state.page = 1
      state.hasMore = true
    },
    SET_LOADING: function (state, val) {
      state.loading = val
    },
    SET_FILTERS: function (state, filters) {
      state.filters = Object.assign({}, state.filters, filters)
    },
    SET_LAST_FETCHED: function (state) {
      state.lastFetched = Date.now()
    },
    SET_PAGE: function (state, page) {
      state.page = page
    },
    SET_HAS_MORE: function (state, val) {
      state.hasMore = val
    }
  },
  actions: {
    FETCH_BEATS: function (context, opts) {
      var reset = opts && opts.reset
      if (reset) {
        context.commit('RESET_PAGINATION')
      } else if (isFresh(context.state.lastFetched) && context.state.items.length > 0) {
        return Promise.resolve()
      }

      context.commit('SET_LOADING', true)
      var f = context.state.filters
      return listBeats({
        page: context.state.page,
        size: 20,
        genre: f.genre,
        tempo: f.tempo
      }).then(function (result) {
        context.commit('APPEND_BEATS', result.items)
        context.commit('SET_HAS_MORE', result.hasMore)
        context.commit('SET_PAGE', context.state.page + 1)
        context.commit('SET_LAST_FETCHED')
        context.commit('SET_LOADING', false)
      }).catch(function () {
        context.commit('SET_LOADING', false)
      })
    },
    LOAD_MORE_BEATS: function (context) {
      if (context.state.hasMore && !context.state.loading) {
        return context.dispatch('FETCH_BEATS')
      }
      return Promise.resolve()
    },
    UPDATE_FILTERS: function (context, filters) {
      context.commit('SET_FILTERS', filters)
      return context.dispatch('FETCH_BEATS', { reset: true })
    }
  }
}

var showcaseModule = {
  namespaced: true,
  state: function () {
    return {
      items: [],
      lastFetched: null
    }
  },
  getters: {
    ALL_SHOWCASE: function (state) {
      return state.items
    }
  },
  mutations: {
    SET_ITEMS: function (state, items) {
      state.items = items
    },
    SET_LAST_FETCHED: function (state) {
      state.lastFetched = Date.now()
    }
  },
  actions: {
    FETCH_SHOWCASE: function (context) {
      if (isFresh(context.state.lastFetched) && context.state.items.length > 0) {
        return Promise.resolve()
      }
      return listSongs({ size: 20, sort: 'createdAt,desc' }).then(function (result) {
        context.commit('SET_ITEMS', result.items)
        context.commit('SET_LAST_FETCHED')
      })
    }
  }
}

var artistsModule = {
  namespaced: true,
  state: function () {
    return {
      items: [],
      lastFetched: null
    }
  },
  getters: {
    ALL_ARTISTS: function (state) {
      return state.items
    }
  },
  mutations: {
    SET_ITEMS: function (state, items) {
      state.items = items
    },
    SET_LAST_FETCHED: function (state) {
      state.lastFetched = Date.now()
    }
  },
  actions: {
    FETCH_ARTISTS: function (context) {
      if (isFresh(context.state.lastFetched) && context.state.items.length > 0) {
        return Promise.resolve()
      }
      return fetchArtists().then(function (items) {
        context.commit('SET_ITEMS', items)
        context.commit('SET_LAST_FETCHED')
      })
    }
  }
}

export default new Vuex.Store({
  state: {
    user: null
  },
  getters: {
    USER: function (state) {
      return state.user
    }
  },
  mutations: {
    SET_USER: function (state, user) {
      state.user = user
    },
    UPDATE_PROFILE: function (state, payload) {
      if (state.user) {
        state.user = Object.assign({}, state.user, payload)
        if (payload.socials) {
          state.user.socials = Object.assign({}, state.user.socials, payload.socials)
        }
      }
    },
    UPDATE_USER_TOKENS: function (state, tokens) {
      if (state.user) {
        state.user = Object.assign({}, state.user, tokens)
      }
    },
    EXIT_USER: function (state) {
      state.user = null
    }
  },
  actions: {
    SET_USER: function (context, payload) {
      context.commit('SET_USER', payload)
      if (payload && payload.accessToken) {
        setAccessToken(payload.accessToken)
      }
      if (payload && payload.refreshToken) {
        setRefreshToken(payload.refreshToken)
      }
      if (payload) {
        context.commit('UPDATE_PROFILE', {
          picture: payload.avatarUrl || payload.picture || '',
          firstName: payload.firstName || '',
          lastName: payload.lastName || ''
        })
      }
      context.dispatch('notifications/FETCH', null, { root: true })
      context.dispatch('uploadDrafts/REFRESH_SUMMARY', null, { root: true })
      context.dispatch('FETCH_PROFILE')
    },
    UPDATE_PROFILE: function (context, payload) {
      context.commit('UPDATE_PROFILE', payload)
    },
    FETCH_PROFILE: function (context) {
      return getMyProfile().then(function (profile) {
        var profileUpdate = {
          userId: profile.id || profile.userId || '',
          firstName: profile.firstName || '',
          lastName: profile.lastName || '',
          displayName: profile.displayName || '',
          email: profile.email || '',
          phone: profile.phone || '',
          alias: profile.alias || '',
          preferredCurrency: profile.preferredCurrency || '',
          socials: profile.socials || {},
          picture: profile.avatarUrl || profile.picture || ''
        }
        if (profile.role) profileUpdate.role = profile.role
        context.commit('UPDATE_PROFILE', profileUpdate)
      })
    },
    SAVE_PROFILE: function (context, payload) {
      return updateProfile(payload).then(function () {
        context.commit('UPDATE_PROFILE', payload)
      })
    },
    UPLOAD_AVATAR: function (context, file) {
      return getAvatarUploadUrl(file.name).then(function (res) {
        return fetch(res.url, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } })
          .then(function (fetchRes) {
            if (!fetchRes.ok) throw new Error('Upload failed: ' + fetchRes.status)
            return res.objectKey
          })
      }).then(function (objectKey) {
        return confirmAvatarUpload(objectKey)
      }).then(function (res) {
        var url = (res && (res.avatarUrl || res.picture)) || ''
        if (url) context.commit('UPDATE_PROFILE', { picture: url })
        return url
      })
    },
    CHANGE_USER_ROLE: function (context, payload) {
      return changeUserRole(payload.userId, payload.role)
    },
    LOG_OUT: function (context) {
      var rt = context.state.user && context.state.user.refreshToken
      apiLogout(rt).catch(function () {})
      context.commit('EXIT_USER')
      context.commit('uploadDrafts/SET_SUMMARY', null, { root: true })
      clearAccessToken()
      clearRefreshToken()
    }
  },
  modules: {
    beats: beatsModule,
    showcase: showcaseModule,
    artists: artistsModule,
    notifications: notificationsModule,
    collaborations: collaborationsModule,
    paymentMethods: paymentMethodsModule,
    uploadDrafts: uploadDraftsModule
  },
  plugins: [vuexLocal.plugin]
})
